import { coreApi } from '$api'
import { DayOfWeek, SelectedShifts, ShiftType } from '$constants'
import {
  CopyRosterRequest,
  IRosterResponse,
  IStaffMemberRosteredShiftsInfo,
  PublishRosterRequest,
  RosterRequest,
  StaffMemberRosteredShiftsInfo,
} from '$models'
import { singletonQueryClient } from '$utils'

import { Dayjs } from 'dayjs'
import sortBy from 'lodash/sortBy'

export enum QueryKeys {
  getById = 'roster-by-id',
  getRosterTemplates = 'roster-templates',
  getRosters = 'rosters',
  getRosterByDateRange = 'roster-by-date-range',
}

/**
 * Publish rosters
 * @param rosterId to publish
 * @param staffMemberRosteredShiftsInfo - is a KeyValue pair where the key is the
 * staffMemberId and the value is the list of roleIds related to that staffMember
 * @param publishRosterToWeb
 * @param lockRoster
 * @param additionalNote
 * @returns Promise<Roster>
 */
export const publish = async (
  rosterId: string,
  staffMemberRosteredShiftsInfo: IStaffMemberRosteredShiftsInfo[],
  publishRosterToWeb: boolean,
  lockRoster: boolean,
  additionalNote: string | undefined,
  selectedShifts: SelectedShifts | undefined
): Promise<IRosterResponse> => {
  const info: StaffMemberRosteredShiftsInfo[] =
    staffMemberRosteredShiftsInfo.map(
      (info) => new StaffMemberRosteredShiftsInfo(info)
    )

  return await coreApi.publishRoster(
    rosterId,
    new PublishRosterRequest({
      lockRoster,
      publishRosterToWeb,
      additionalNote,
      staffMemberRosteredShiftsInfo: info,
      selectedShifts,
    })
  )
}

/**
 * Get Roster by Id
 * @param rosterId - roster id to get
 * @returns Promise<Roster>
 */
export const getById = async (rosterId: string): Promise<IRosterResponse> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.getById, rosterId],
    queryFn: async ({ signal }) => {
      const rosters = await coreApi.getRoster(rosterId, signal)

      return rosters
    },
  })
}

/**
 * Get Rosters by date range
 * @param startTime
 * @param endTime
 * @returns Promise<Roster>
 */
export const getRostersByDateRange = async (
  startTime: Dayjs,
  endTime: Dayjs
): Promise<IRosterResponse[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.getRosterByDateRange, startTime, endTime],
    queryFn: async ({ signal }) => {
      const rosters = await coreApi.getRostersByDateRange(
        startTime,
        endTime,
        signal
      )

      return rosters
    },
  })
}

/**
 * Get Roster Templates
 * @param includeDeleted
 * @returns Promise<RosterTemplate[]>
 */
export const getTemplates = async (
  includeDeleted: boolean,
  shiftTypesToInclude: ShiftType[] = []
): Promise<IRosterResponse[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getRosterTemplates,
      includeDeleted,
      shiftTypesToInclude,
    ],
    queryFn: async ({ signal }) => {
      const rosterTemplates = await coreApi.getRosterTemplates(
        includeDeleted,
        shiftTypesToInclude,
        signal
      )

      return sortBy<IRosterResponse>(rosterTemplates, 'templateName')
    },
  })
}

/**
 * Create roster template
 * @returns Promise<RosterTemplate>
 */
export const createRosterTemplate = async (): Promise<IRosterResponse> => {
  return await coreApi.createRosterTemplate()
}

/**
 * Update roster template
 * @returns Promise<RosterTemplate>
 */
export const updateRosterTemplate = async (
  rosterTemplate: IRosterResponse
): Promise<IRosterResponse> => {
  return await coreApi.updateRoster(
    rosterTemplate.id!,
    new RosterRequest(rosterTemplate)
  )
}

/**
 * Create roster template
 * @returns Promise<RosterTemplate>
 */
export const copyRosterTemplate = async (
  rosterId: string,
  templateName: string
): Promise<IRosterResponse> => {
  return await coreApi.copyRosterTemplate({
    id: rosterId,
    templateName,
  } as RosterRequest)
}

/**
 *
 * @param rosterTemplateId - roster template id (if the copy is done from a roster template)
 * @param sourceWeekStart - srouce week start date (if the copy is done from a week range)
 * @param destinationWeekStart - destination week start date
 * @param days - array of days of the week that needs to be copied to the new roster
 * @param roleIds - either null or all roles should be copied, or an array of role ids that
 *   should be copied to the destination week
 * @returns Promise<void>
 */
export const copyRoster = async (
  rosterTemplateId: string | undefined,
  sourceWeekStart: Dayjs | undefined,
  destinationWeekStart: Dayjs,
  days: DayOfWeek[],
  roleIds: string[] | undefined
): Promise<void> => {
  return await coreApi.copyRoster(
    new CopyRosterRequest({
      rosterTemplateId,
      sourceWeekStart,
      destinationWeekStart,
      days,
      roleIds,
    })
  )
}

/**
 * Sets the public roster token setting through the rosters controller
 * @param token string uuid token
 * @returns void
 */
export const updatePublicToken = async (token: string): Promise<void> => {
  return coreApi.updatePublicRosterToken(token)
}
